import React, { useEffect, useState, useRef } from 'react';
import styles from '../../styles/PortalEstudiantesStyles/PortalEstudiantes.module.css';

/**
 * Modal de espera de pago Webpay
 * Props:
 * - referencia: string
 * - onClose: function
 * - onSuccess: function (opcional, recibe info de pago)
 */

function EsperandoPagoModal({ referencia, onClose, onSuccess }) {
  const [status, setStatus] = useState('pending'); // 'pending', 'success', 'timeout', 'error', 'no-aprobado'
  const [mensaje, setMensaje] = useState('Esperando confirmación de pago...');
  const [intentos, setIntentos] = useState(0);
  const [infoPago, setInfoPago] = useState(null);
  const [mensajeDescarga, setMensajeDescarga] = useState('Descargando recibo PDF...');
  const timerRef = useRef(null);
  const maxIntentos = Math.ceil((5 * 60) / 7); // 5 minutos, cada 7 segundos
  // Para evitar doble descarga
  const pdfDescargadoRef = useRef(false);
  // Estado para forzar descarga manual
  const [descargaManual, setDescargaManual] = useState(false);

  // Suscripción SSE para escuchar el estado del pago en tiempo real
  useEffect(() => {
    if (!referencia) return;
    let eventSource;
    setMensaje('Esperando confirmación de pago...');
    setStatus('pending');
    setInfoPago(null);
    setIntentos(0);
    setDescargaManual(false);
    pdfDescargadoRef.current = false;

    const url = `/api/financiero/sse_estado_pago/${referencia}/`;
    eventSource = new window.EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.estado === 'APLICADO') {
          setStatus('success');
          setMensaje('¡Pago aprobado correctamente!');
        } else if (data.estado === 'RECHAZADO' || data.estado === 'CANCELADO') {
          setStatus('no-aprobado');
          setMensaje('El pago no está aprobado. Estado: ' + data.estado);
        } else if (data.estado === 'NO_ENCONTRADO') {
          setStatus('error');
          setMensaje('No se encontró la referencia de pago.');
        } else if (data.error) {
          setStatus('error');
          setMensaje('Error al consultar el estado del pago.');
        }
      } catch (e) {
        setStatus('error');
        setMensaje('Error al procesar la respuesta del servidor.');
      }
    };

    eventSource.addEventListener('end', () => {
      eventSource.close();
    });

    eventSource.onerror = () => {
      setStatus('error');
      setMensaje('Error de conexión con el servidor.');
      eventSource.close();
    };

    return () => {
      if (eventSource) eventSource.close();
    };
    // eslint-disable-next-line
  }, [referencia]);

  // Descargar PDF cuando status sea 'success' y no se haya descargado
  useEffect(() => {
    async function descargarPDF() {
      if (pdfDescargadoRef.current) return;
      pdfDescargadoRef.current = true;
      let student = null;
      let charges = [];
      let paymentData = {};
      // Si hay infoPago con datos completos, úsala; si no, consulta al backend
      if (infoPago && infoPago.student && infoPago.charges && infoPago.paymentData) {
        student = infoPago.student;
        charges = infoPago.charges;
        paymentData = infoPago.paymentData;
      } else {
        // Petición adicional para obtener datos completos del recibo
        try {
          const resp = await fetch(`/api/financiero/recibo?referencia=${referencia}`);
          const recibo = await resp.json();
          student = recibo.student;
          charges = recibo.charges;
          paymentData = recibo.paymentData;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error al obtener datos del recibo:', e);
        }
      }
      // Importar función para generar PDF
      try {
        const { generateReciboPDF } = await import('../../utils/generateReciboPDF');
        // Ajustar datos solo para el PDF de portal estudiantes:
        // - Forma de pago fija "PAGO ONLINE"
        // - Cajero vacío y sin usuario_creacion (para que no intente resolverlo)
        const paymentDataForPdf = {
          ...paymentData,
          forma_pago: 'PAGO ONLINE',
          cajero: '',
          usuario_creacion: null,
        };
        const dataUriString = await generateReciboPDF({
          student,
          charges,
          paymentData: paymentDataForPdf,
          descargar: true,
        });

        // Intentar abrir el PDF en una nueva pestaña
        if (dataUriString && typeof window !== 'undefined') {
          try {
            window.open(dataUriString, '_blank');
          } catch (err) {
            // Si el navegador bloquea el popup, al menos queda la descarga
            // eslint-disable-next-line no-console
            console.warn('No se pudo abrir el PDF en una nueva pestaña:', err);
          }
        }
        // Enviar recibo PDF por email al email_institucional del estudiante
        const emailEstudiante = student?.email_institucional || student?.email;
        if (emailEstudiante && dataUriString) {
          try {
            const pdfBase64 = dataUriString.split(',')[1] || dataUriString;
            const emailResp = await fetch('/api/financiero/cargos-manuales/enviar-recibo/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to_email: emailEstudiante,
                pdf_base64: pdfBase64,
                subject: 'Recibo de pago - Universidad Lux',
              }),
            });
            if (emailResp.ok) {
              setMensajeDescarga('¡Recibo descargado y enviado por correo!');
            } else {
              const errData = await emailResp.json().catch(() => ({}));
              // eslint-disable-next-line no-console
              console.error('Error al enviar recibo por correo:', emailResp.status, errData);
              setMensajeDescarga('¡Recibo descargado! (Error al enviar por correo)');
            }
          } catch (emailErr) {
            // eslint-disable-next-line no-console
            console.error('No se pudo enviar el recibo por correo:', emailErr);
            setMensajeDescarga('¡Recibo descargado con éxito!');
          }
        } else {
          setMensajeDescarga('¡Recibo descargado con éxito!');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        setMensajeDescarga('Error al descargar el recibo.');
        console.error('Error al generar PDF:', e);
      }
    }
    if (status === 'success' && !descargaManual) {
      setMensajeDescarga('Descargando recibo PDF...');
      descargarPDF();
    }
  }, [status, infoPago, referencia, descargaManual]);

  // Función para descarga manual
  const handleDescargarManual = async () => {
    pdfDescargadoRef.current = false;
    setDescargaManual(true);
    setMensajeDescarga('Descargando recibo PDF...');
    setTimeout(() => setDescargaManual(false), 1000);
  };

  const handleVerificarManual = async () => {
    setMensaje('Verificando pago...');
    setStatus('pending');
    setIntentos(0);
    pdfDescargadoRef.current = false;
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Esperando Pago</h3>
        </div>
        <div className={styles.modalBody} style={{ position: 'relative', minHeight: 120 }}>
          <div style={{ margin: '1rem 0', textAlign: 'center' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <img src="/lux_black.svg" alt="Universidad Lux" style={{ height: 40 }} />
            </div>
            {status === 'pending' && (
              <div style={{ marginBottom: '0.75rem', color: '#444' }}>
                {mensaje}
              </div>
            )}
            <div><strong>Referencia:</strong></div>
            <div style={{ fontSize: '1.2em', letterSpacing: '2px', margin: '0.5em 0' }}>{(referencia || '').replace(/(.{4})/g, '$1 ').trim()}</div>
            {status === 'success' && (
              <>
                <div style={{ color: 'green', marginTop: '1em' }}>
                  ¡Pago aprobado correctamente! <br /> {mensajeDescarga}
                </div>
                <button
                  className={styles.confirmButton}
                  onClick={handleDescargarManual}
                  style={{ marginTop: '1em' }}
                >
                  Descargar
                </button>
              </>
            )}
            {status === 'no-aprobado' && (
              <div style={{ color: 'orange', marginTop: '1em' }}>{mensaje}</div>
            )}
            {status === 'timeout' && (
              <div style={{ color: 'orange', marginTop: '1em' }}>No se detectó el pago en 5 minutos.</div>
            )}
            {status === 'error' && (
              <div style={{ color: 'red', marginTop: '1em' }}>Ocurrió un error al consultar el pago.</div>
            )}
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={() => onClose(infoPago)}>Cerrar</button>
          {status !== 'success' && (
            <>
              <button className={styles.cancelButton} style={{marginLeft: '0.5em'}} onClick={onClose}>
                Cancelar operación
              </button>
              <button className={styles.confirmButton} onClick={handleVerificarManual} disabled={status === 'pending'}>
                Verificar manualmente
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EsperandoPagoModal;
